import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Cashpurr } from '@/components/ui/cashpurr';
import { dataService } from '@/services/dataService';
import { useAuth } from '@/hooks/useAuth';

interface TutorialStep {
  id: string;
  title: string;
  content: string;
  target?: string;
  cashpurrMessage: string;
  cashpurrMood: 'happy' | 'thinking' | 'excited';
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to StartupStreak! 🚀',
    content: 'Meow! I\'m Cashpurr, your AI companion for the next 30 days. I\'ll help you turn your startup idea into your first paying customer!',
    cashpurrMessage: 'Ready to sprint to success? Let\'s start this journey together! 💰',
    cashpurrMood: 'excited'
  },
  {
    id: 'dashboard',
    title: 'Track Your 30-Day Sprint',
    content: 'This is your mission control! Track your progress, see your streak, and stay motivated with daily tasks.',
    target: '[data-tutorial="dashboard"]',
    cashpurrMessage: 'Every day counts in your sprint. I\'ll keep you accountable and celebrate your wins! 🎯',
    cashpurrMood: 'happy'
  },
  {
    id: 'idea-validation',
    title: 'Validate Your Idea with AI',
    content: 'Before you build anything, let\'s make sure your idea has legs! Get AI-powered feedback and market insights.',
    target: '[data-tutorial="idea-validation"]',
    cashpurrMessage: 'A validated idea is worth its weight in gold. Let\'s make sure you\'re building something people actually want! 💡',
    cashpurrMood: 'thinking'
  },
  {
    id: 'boss-mode',
    title: 'Boss Mode: Daily Accountability',
    content: 'This is where the magic happens! Daily and weekly tasks to keep you moving forward, plus streak tracking.',
    target: '[data-tutorial="boss-mode"]',
    cashpurrMessage: 'Consistency beats perfection. Small daily actions lead to big results! 💪',
    cashpurrMood: 'excited'
  },
  {
    id: 'roi-tracker',
    title: 'Track Every Penny',
    content: 'Log your expenses and revenue to see exactly when you hit profitability. Knowledge is power!',
    target: '[data-tutorial="roi-tracker"]',
    cashpurrMessage: 'Watch those numbers like a hawk! Every dollar tracked is a dollar optimized. 📊',
    cashpurrMood: 'thinking'
  },
  {
    id: 'community',
    title: 'Join Your Pod',
    content: 'You\'ve been automatically added to the Product Builders pod! Connect with fellow founders for support and accountability.',
    target: '[data-tutorial="community"]',
    cashpurrMessage: 'The journey is better together! Your pod mates are here to cheer you on and share wisdom. 🤝',
    cashpurrMood: 'happy'
  },
  {
    id: 'journal',
    title: 'Reflect and Grow',
    content: 'Document your founder journey with AI-powered insights. Track your thoughts, challenges, and breakthroughs.',
    target: '[data-tutorial="journal"]',
    cashpurrMessage: 'Reflection is the key to growth. I\'ll help you spot patterns and celebrate progress! 📝',
    cashpurrMood: 'thinking'
  },
  {
    id: 'complete',
    title: 'You\'re All Set! 🎉',
    content: 'That\'s the tour! Remember, I\'m always here if you need help. Let\'s turn that idea into revenue!',
    cashpurrMessage: 'Time to sprint! Your first customer is just 30 days away. Let\'s make it happen! 🏃‍♂️💨',
    cashpurrMood: 'excited'
  }
];

interface TutorialSystemProps {
  onComplete: () => void;
  onSkip: () => void;
}

const TutorialSystem: React.FC<TutorialSystemProps> = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const { user } = useAuth();

  const currentStepData = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    if (user) {
      await dataService.updateUserProfile(user.id, { tutorial_completed: true });
    }
    setIsVisible(false);
    onComplete();
  };

  const handleSkip = async () => {
    if (user) {
      await dataService.updateUserProfile(user.id, { tutorial_completed: true });
    }
    setIsVisible(false);
    onSkip();
  };

  useEffect(() => {
    // Scroll target element into view if it exists
    if (currentStepData.target) {
      const element = document.querySelector(currentStepData.target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentStep, currentStepData.target]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-startup text-white p-6 text-center">
          <div className="flex justify-center mb-4">
            <Cashpurr size="lg" mood={currentStepData.cashpurrMood} />
          </div>
          <h2 className="text-xl font-bold mb-2">{currentStepData.title}</h2>
          <div className="flex justify-center space-x-1">
            {tutorialSteps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-startup-gray leading-relaxed mb-4">
              {currentStepData.content}
            </p>
            
            {/* Cashpurr Message */}
            <div className="bg-startup-slate rounded-lg p-4 flex items-start space-x-3">
              <Cashpurr size="sm" mood={currentStepData.cashpurrMood} />
              <div className="bg-white rounded-lg p-3 shadow-sm flex-1">
                <p className="text-sm text-startup-navy">
                  {currentStepData.cashpurrMessage}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              {currentStep > 0 && (
                <Button variant="outline" onClick={handlePrevious} size="sm">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex space-x-2">
              <Button variant="ghost" onClick={handleSkip} size="sm" className="text-startup-gray">
                Skip Tour
              </Button>
              <Button onClick={handleNext} className="bg-startup-teal text-white">
                {isLastStep ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Let's Go!
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default TutorialSystem;
